// FTEDN — Product store (Zustand)
import { create } from 'zustand';
import { apiClient } from '../hooks/useApi';

export interface ProductDetail {
  id: string;
  name: string;
  slug: string;
  tagline: string;
  description: string;
  status: string;
  category: string;
  imageUrl?: string;
  ingredientList: string[];
  stats: {
    voteCount: number;
    ideaCount: number;
    feedbackCount: number;
    testerCount: number;
    reviewCount: number;
  };
  publicTimeline: TimelineEntry[];
}

export interface TimelineEntry {
  id: string;
  title: string;
  description?: string;
  type?: string;
  addedAt: string;
}

export interface JournalEntry {
  id: string;
  date: string;
  notes: string;
  photoUrl?: string;
  day: number;
}

export interface ProductReview {
  rating: number;
  content: string;
  productId: string;
}

interface ProductState {
  products: ProductDetail[];
  activeProduct: ProductDetail | null;
  journal: JournalEntry[];
  loading: boolean;
  error: string | null;
  fetchProducts: () => Promise<void>;
  fetchProduct: (id: string) => Promise<void>;
  submitReview: (review: ProductReview) => Promise<boolean>;
  addJournalEntry: (entry: Omit<JournalEntry, 'id'>) => void;
}

/** Map raw Firestore product to our client shape */
export function mapProduct(raw: Record<string, unknown>): ProductDetail {
  const stats = (raw.stats as Record<string, unknown>) ?? {};
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    slug: String(raw.slug ?? ''),
    tagline: String(raw.tagline ?? ''),
    description: String(raw.description ?? ''),
    status: String(raw.status ?? 'concept'),
    category: String(raw.category ?? ''),
    imageUrl: raw.imageUrl as string | undefined,
    ingredientList: Array.isArray(raw.ingredientList) ? raw.ingredientList.map(String) : [],
    stats: {
      voteCount: Number(stats.voteCount ?? 0),
      ideaCount: Number(stats.ideaCount ?? 0),
      feedbackCount: Number(stats.feedbackCount ?? 0),
      testerCount: Number(stats.testerCount ?? 0),
      reviewCount: Number(stats.reviewCount ?? 0),
    },
    publicTimeline: Array.isArray(raw.publicTimeline)
      ? (raw.publicTimeline as Record<string, unknown>[]).map(mapTimeline)
      : [],
  };
}

/** Map raw timeline entry */
export function mapTimeline(raw: Record<string, unknown>): TimelineEntry {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: raw.description as string | undefined,
    type: raw.type as string | undefined,
    addedAt: String(raw.addedAt ?? ''),
  };
}

/** Validate a product review before submission */
export function validateReview(review: ProductReview): { valid: true } | { valid: false; error: string } {
  if (!review.productId) {
    return { valid: false, error: 'Product ID required' };
  }
  if (!Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5) {
    return { valid: false, error: 'Rating must be 1-5' };
  }
  if (!review.content.trim()) {
    return { valid: false, error: 'Please write a review' };
  }
  if (review.content.trim().length < 20) {
    return { valid: false, error: 'Review must be at least 20 characters' };
  }
  if (review.content.length > 3000) {
    return { valid: false, error: 'Review must be under 3000 characters' };
  }
  return { valid: true };
}

/** Format a journal date for display */
export function formatJournalDay(startDate: string, entryDate: string): number {
  const start = new Date(startDate).getTime();
  const entry = new Date(entryDate).getTime();
  if (isNaN(start) || isNaN(entry)) return 1;
  return Math.max(1, Math.floor((entry - start) / (1000 * 60 * 60 * 24)) + 1);
}

export const useProductStore = create<ProductState>((set, get) => ({
  products: [],
  activeProduct: null,
  journal: [],
  loading: false,
  error: null,

  fetchProducts: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get('/products');
      if (res.ok && Array.isArray(res.products)) {
        set({ products: res.products.map(mapProduct), loading: false });
      } else {
        set({ error: 'Failed to load products', loading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchProduct: async (id) => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get(`/products/${id}`);
      if (res.ok && res.product) {
        set({ activeProduct: mapProduct(res.product), loading: false });
      } else {
        set({ error: 'Product not found', loading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  submitReview: async (review) => {
    try {
      const res = await apiClient.post('/feedback/review', review);
      return res.success === true || res.ok === true;
    } catch {
      return false;
    }
  },

  addJournalEntry: (entry) => {
    const id = `j-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    set({ journal: [...get().journal, { ...entry, id }] });
  },
}));
